import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../App';
import BottomNav from '../components/BottomNav';
import SearchBar from '../components/SearchBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function ArticleDetailPage() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [reference, setReference] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [articleId]);

  const fetchData = async () => {
    try {
      // Fetch sections for this article
      const sectionsResponse = await api.get(`/articles/${articleId}/sections`);
      setSections(sectionsResponse.data);

      // Get article details from the first section's data
      if (sectionsResponse.data.length > 0) {
        const sectionData = sectionsResponse.data[0];
        
        // Fetch reference details
        const refResponse = await api.get(`/references/${sectionData.reference_id}`);
        setReference(refResponse.data);

        // Get all articles to find the current one
        const articlesResponse = await api.get(`/references/${sectionData.reference_id}/articles`);
        const currentArticle = articlesResponse.data.find(a => a.id === articleId);
        setArticle(currentArticle);
      }
    } catch (error) {
      toast.error('Failed to load article details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
            <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 dark:text-slate-300 dark:hover:bg-slate-700"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        {/* Article Header */}
        <div className="mb-8">
          {reference && (
            <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mb-2">{reference.short_name}</p>
          )}
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            {article.number}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400">{article.title}</p>
        </div>

        {/* Search Bar */}
        <SearchBar />

        {/* Sections List */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            Sections
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
            Browse through the sections that comprise this article. Each section contains detailed provisions and legal text
          </p>

          {sections.length === 0 ? (
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-8 text-center text-slate-600 dark:text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-400 dark:text-slate-600" />
                <p>Sections content will be available soon.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {sections.map((section) => (
                <Card
                  key={section.id}
                  className="hover:shadow-lg transition-all cursor-pointer border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-600 dark:bg-slate-800"
                  onClick={() => navigate(`/sections/${section.id}`)}
                  data-testid={`section-${section.id}`}
                >
                  <CardHeader>
                    <CardTitle className="text-emerald-700 dark:text-emerald-400">
                      {section.number}: {section.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
                      size="sm"
                      data-testid={`view-section-${section.id}`}
                    >
                      View Content
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
